var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import axios from 'axios';
import { CancelablePromise } from 'packages/core/CancelablePromise';
import PathBuilder from 'packages/core/PathBuilder';
import { Entity, EntityBuilder } from '@decahedron/entity';
import { Cursoring } from 'packages/core/Cursoring';
import { Pagination } from 'packages/core/Pagination';
var AbstractRepository = /** @class */ (function () {
    function AbstractRepository() {
        this.responseTransformer = function (response) { return response.data; };
        this.baseUrl = null;
        this.axios = axios;
    }
    AbstractRepository.prototype.getOne = function (id, params, query) {
        var _this = this;
        params = (params) ? __assign({ id: id }, params) : { id: id };
        var url = !this.url.includes('/:id') ? this.url + "/:id" : this.url;
        return this.get(url, params, query)
            .then(function (response) { return EntityBuilder.buildOne(_this.entity, _this.responseTransformer(response).data); });
    };
    // Find is here to fulfill the IRepository interface requirements and to simply use that everywhere publicly without declaring find in each repo separately.
    AbstractRepository.prototype.find = function (id, params, query) {
        return this.getOne(id, params, query);
    };
    AbstractRepository.prototype.getPaginated = function (url, params, query) {
        var _this = this;
        return this.get(url, params, query)
            .then(function (response) {
            var result = _this.responseTransformer(response);
            return {
                items: EntityBuilder.buildMany(_this.entity, result.data),
                pagination: EntityBuilder.buildOne(Pagination, result.pagination || result.meta),
            };
        });
    };
    AbstractRepository.prototype.getCursored = function (params, query) {
        var _this = this;
        return this.get(this.url, params, query)
            .then(function (response) {
            var _a;
            var result = _this.responseTransformer(response);
            var cursor;
            if ((_a = result.links) === null || _a === void 0 ? void 0 : _a.next) {
                cursor = result.links.next.match(/id=([^&]*)/)[1];
            }
            return {
                items: EntityBuilder.buildMany(_this.entity, result.data),
                pagination: EntityBuilder.buildOne(Cursoring, result.pagination),
                cursor: cursor,
            };
        });
    };
    AbstractRepository.prototype.update = function (id, data, params) {
        return this.put(this.url + "/:id", data, __assign({ id: id }, params));
    };
    AbstractRepository.prototype.create = function (data, params) {
        var _this = this;
        var payload = (data instanceof Entity) ? data.toJson() : data;
        var config = (this.baseUrl) ? { baseURL: this.baseUrl } : null;
        return this.axios.post(PathBuilder(this.url, params), payload, config)
            .then(function (response) {
            var result = _this.responseTransformer(response);
            return EntityBuilder.buildOne(_this.entity, result.data);
        });
    };
    AbstractRepository.prototype.delete = function (id, params, useDirectUrl) {
        var config = (this.baseUrl) ? { baseURL: this.baseUrl } : null;
        if (useDirectUrl) {
            return this.axios.delete(PathBuilder(id, __assign({ id: id }, params)), config);
        }
        return this.axios.delete(PathBuilder(this.url + "/:id", __assign({ id: id }, params)), config);
    };
    AbstractRepository.prototype.get = function (url, params, query) {
        var _this = this;
        var cancelSource = axios.CancelToken.source();
        var config = {
            cancelToken: cancelSource.token,
        };
        if (this.baseUrl) {
            config.baseURL = this.baseUrl;
        }
        // Thanks to this https://madhatted.com/2013/6/16/you-do-not-understand-browser-history
        // we add a unique string to cache bust browser history back
        var requestPromise = new CancelablePromise(function (resolve, reject) {
            _this.axios.get(PathBuilder(url, params, __assign({ c: Date.now().toString(16) }, query)), config)
                .then(function (response) { return resolve(response); })
                .catch(function (reason) { return reject(reason); });
        });
        requestPromise.cancel = function () {
            cancelSource.cancel();
            requestPromise.reject('canceled');
        };
        return requestPromise;
    };
    AbstractRepository.prototype.getAll = function (url, params, query) {
        var _this = this;
        return this.get(url, params, query)
            .then(function (response) {
            var result = _this.responseTransformer(response);
            return {
                items: EntityBuilder.buildMany(_this.entity, result.data),
                pagination: null,
            };
        });
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AbstractRepository.prototype.put = function (url, data, params) {
        var _this = this;
        var payload = (data instanceof Entity) ? data.toJson() : data;
        var config = (this.baseUrl) ? { baseURL: this.baseUrl } : null;
        return this.axios.put(PathBuilder(url, params), payload, config)
            .then(function (response) {
            var result = _this.responseTransformer(response);
            return EntityBuilder.buildOne(_this.entity, result.data);
        });
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AbstractRepository.prototype.post = function (url, data, params) {
        var _this = this;
        var payload = (data instanceof Entity) ? data.toJson() : data;
        var config = (this.baseUrl) ? { baseURL: this.baseUrl } : null;
        return this.axios.post(PathBuilder(url, params), payload, config)
            .then(function (response) {
            var result = _this.responseTransformer(response);
            // support a null entity if the repository wants to do all decoding
            return _this.entity ? EntityBuilder.buildOne(_this.entity, result.data) : result;
        });
    };
    return AbstractRepository;
}());
export { AbstractRepository };
